import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Sparkles, FileScan, BookOpen, FileText, Wrench, Receipt, PenTool,
  Loader2, Download, Send, CheckCircle2, ShieldCheck, Zap, Smartphone,
} from "lucide-react";

/**
 * TrueLine sales presentation — beautiful, visual page for showing to
 * prospective clients. Admin can personalize with the prospect's name and
 * either download a branded PDF or email it directly via Resend.
 */
export default function Presentation() {
  const [form, setForm] = useState({
    prospect_name: "", prospect_company: "", prospect_email: "", prospect_phone: "",
    notes: "", also_send_email: false,
  });
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (!form.prospect_name.trim()) return toast.error("Digite o nome do cliente/prospect");
    setBusy(true);
    try {
      const r = await api.post("/presentation/generate", form, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `TrueLine-Overview-${form.prospect_name.replace(/\s+/g, "-")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      const status = r.headers?.["x-email-status"];
      if (form.also_send_email && status && status !== "not_sent") {
        toast.success(`PDF baixado · Email ${status === "sent" ? "enviado" : status}`);
      } else {
        toast.success("PDF gerado — prospect salvo no CRM");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar PDF");
    } finally {
      setBusy(false);
    }
  };

  const modules = [
    { icon: FileScan, name: "Estimate Analyzer", desc: "Sobe o PDF do body shop, extrai VIN + linhas, detecta todas as calibrações necessárias" },
    { icon: BookOpen, name: "OEM Library", desc: "Procedimentos OEM ao vivo via IA — sem depender de ALLDATA a $99/mês" },
    { icon: FileText, name: "Quote Wizard", desc: "Orçamento em 60s com rate table por operação + tipo (static/dynamic/dual)" },
    { icon: Wrench, name: "Work Order Queue", desc: "Todo orçamento fechado vira Work Order no PWA do técnico — scan QR, foto, assinatura" },
    { icon: Receipt, name: "Billing + Comissões", desc: "Invoices para body shop e customer, comissão do vendedor, Stripe live, export QuickBooks" },
    { icon: PenTool, name: "E-Signature", desc: "Link mágico — cliente assina no celular, IP + SHA-256 + timestamp (ESIGN Act)" },
  ];
  const reasons = [
    { icon: Zap, title: "Uma plataforma", desc: "OCR, VIN, OEM library, quoting, invoicing, mobile app, PDF assinatura, Stripe — tudo integrado" },
    { icon: CheckCircle2, title: "Pagamento mais rápido", desc: "Body shop aceita no portal · Invoice pronta quando o técnico fecha a WO" },
    { icon: ShieldCheck, title: "Zero drift de dados", desc: "Cliente, body shop e veículo sincronizados em Master Data, CRM, Quotes, Jobs e Billing" },
    { icon: Smartphone, title: "Mobile-first", desc: "PWA instalável · técnico executa cards, escaneia QR e imprime WO direto do Schedule" },
  ];

  return (
    <div className="space-y-12 max-w-6xl mx-auto animate-slide-up pb-16" data-testid="presentation-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-blue-500/30 bg-gradient-to-br from-[#0B1120] via-[#0B1120] to-blue-900/20 p-10 md:p-16">
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{ backgroundImage: "radial-gradient(1200px circle at 20% 10%, rgba(37,99,235,0.25), transparent), radial-gradient(800px circle at 80% 90%, rgba(16,185,129,0.15), transparent)" }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/40 bg-blue-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-bold text-blue-300 mb-6">
            <Sparkles className="w-3 h-3" />
            Apresentação Comercial
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-semibold tracking-tight leading-[0.95]">
            A plataforma <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">ADAS</span>
            <br />feita para quem <span className="text-emerald-400">executa</span>.
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mt-6 leading-relaxed">
            Do estimate do body shop ao pagamento na Stripe, tudo em <strong className="text-white">uma única plataforma</strong>. Sem planilha, sem ALLDATA, sem drift de dados.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-10 max-w-2xl">
            {[
              { kpi: "30+", label: "Operações ADAS" },
              { kpi: "100%", label: "OEM compliance" },
              { kpi: "<24h", label: "Turnaround" },
            ].map((k) => (
              <div key={k.kpi} className="rounded-xl border border-slate-800 bg-slate-900/50 backdrop-blur px-4 py-3">
                <div className="font-display text-3xl font-semibold text-blue-300 font-mono">{k.kpi}</div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-1">{k.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Who we are */}
      <section>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">Quem somos</div>
        <h2 className="font-display text-4xl font-semibold tracking-tight mb-4">Construído por quem calibra.</h2>
        <p className="text-slate-300 leading-relaxed max-w-3xl">
          A TrueLine nasceu no chão do body shop — dos mesmos técnicos e vendedores que gastavam horas fazendo estimate, buscando procedimentos OEM na ALLDATA, montando invoice à mão e correndo atrás de comissão perdida. Hoje é a única plataforma de operações desenhada especificamente para o negócio de <strong className="text-white">Auto Glass + ADAS Calibration</strong>.
        </p>
      </section>

      {/* What we do */}
      <section>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">O que fazemos</div>
        <h2 className="font-display text-4xl font-semibold tracking-tight mb-8">Seis módulos, uma operação.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((m) => (
            <div key={m.name} className="group rounded-2xl border border-slate-800 bg-[#0B1120] p-6 hover:border-blue-500/40 hover:bg-blue-500/5 transition-all">
              <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <m.icon className="w-5 h-5 text-blue-300" />
              </div>
              <div className="font-display text-xl font-medium">{m.name}</div>
              <p className="text-sm text-slate-400 leading-relaxed mt-2">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">Por que TrueLine</div>
        <h2 className="font-display text-4xl font-semibold tracking-tight mb-8">Diferenciais reais.</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {reasons.map((r) => (
            <div key={r.title} className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6 flex gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
                <r.icon className="w-5 h-5 text-emerald-300" />
              </div>
              <div>
                <div className="font-display text-lg font-medium text-emerald-100">{r.title}</div>
                <p className="text-sm text-slate-300 leading-relaxed mt-1">{r.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Personalization form */}
      <section className="rounded-3xl border border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-transparent p-8 md:p-12" data-testid="presentation-form">
        <div className="text-xs uppercase tracking-[0.22em] text-blue-300 font-bold mb-3">Envie para um cliente</div>
        <h2 className="font-display text-3xl font-semibold tracking-tight mb-2">Personalize e envie.</h2>
        <p className="text-slate-400 mb-8">
          Preenchemos automaticamente o nome do cliente na capa do PDF e salvamos ele como <em>prospect</em> no CRM. Você pode baixar o PDF ou já enviar por email.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Nome do cliente *</Label>
            <Input data-testid="prospect-name" value={form.prospect_name} onChange={(e) => setForm({ ...form, prospect_name: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" placeholder="Ex: John Doe" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Empresa</Label>
            <Input data-testid="prospect-company" value={form.prospect_company} onChange={(e) => setForm({ ...form, prospect_company: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" placeholder="Ex: Awesome Body Shop" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Email</Label>
            <Input data-testid="prospect-email" type="email" value={form.prospect_email} onChange={(e) => setForm({ ...form, prospect_email: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" placeholder="john@example.com" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Telefone</Label>
            <Input data-testid="prospect-phone" value={form.prospect_phone} onChange={(e) => setForm({ ...form, prospect_phone: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" placeholder="(555) 123-4567" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Notas internas</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" placeholder="Contexto — onde conheceu, follow-up etc." />
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs cursor-pointer ${form.also_send_email ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300" : "border-slate-800 bg-[#0B1120] text-slate-400"}`}>
            <input type="checkbox" checked={form.also_send_email} onChange={(e) => setForm({ ...form, also_send_email: e.target.checked })} className="w-4 h-4 accent-emerald-500" />
            Também enviar por email agora
          </label>
          <div className="flex-1" />
          <Button onClick={download} disabled={busy} className="bg-blue-600 hover:bg-blue-500 h-12 px-6 text-base font-semibold" data-testid="generate-pdf-btn">
            {busy ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : form.also_send_email ? <Send className="w-5 h-5 mr-2" /> : <Download className="w-5 h-5 mr-2" />}
            {form.also_send_email ? "Enviar + Baixar" : "Baixar PDF"}
          </Button>
        </div>
      </section>

      {/* Contact */}
      <section className="rounded-2xl border border-slate-800 bg-[#0B1120] p-8 text-center">
        <div className="font-display text-2xl font-semibold">Vamos conversar</div>
        <p className="text-slate-400 mt-2">30 minutos, sem apresentação genérica. Trazemos seu estimate real e mostramos rodando.</p>
        <div className="mt-4 flex justify-center gap-6 text-sm text-slate-300">
          <a href="mailto:contact@trueadascalibration.com" className="hover:text-blue-300">contact@trueadascalibration.com</a>
          <span className="text-slate-600">·</span>
          <span>(774) 231-8377</span>
          <span className="text-slate-600">·</span>
          <a href="https://trueadascalibration.com" className="hover:text-blue-300" target="_blank" rel="noreferrer">trueadascalibration.com</a>
        </div>
      </section>
    </div>
  );
}
